import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface Option { value: string; label: string }

interface MultiSelectProps {
  label?: string;
  placeholder?: string;
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
  includeDynamicOthers?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  native?: boolean;
  portal?: boolean;
  showSearchButton?: boolean;
  allowSearch?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  placeholder = "Select...",
  options,
  value,
  onChange,
  required,
  includeDynamicOthers = false,
  disabled = false,
  error,
  className = "",
  native = false,
  portal = false,
  showSearchButton = true,
  allowSearch = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const searchTerm = query.toLowerCase().trim();
    return options.filter(option => 
      option.label.toLowerCase().includes(searchTerm) ||
      option.value.toLowerCase().includes(searchTerm)
    );
  }, [options, query]);

  const finalOptions = useMemo(() => {
    if (filtered.length > 0) return filtered;
    if (includeDynamicOthers && query.trim()) {
      return [{ value: 'OTHERS', label: `Others (${query})` }];
    }
    return filtered;
  }, [filtered, includeDynamicOthers, query]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [finalOptions]);

  const handleOptionSelect = useCallback(
    (option: Option) => {
      const newValue = Array.isArray(value) ? [...value] : [];
      const index = newValue.indexOf(option.value);
      if (index > -1) {
        newValue.splice(index, 1);
      } else {
        newValue.push(option.value);
      }
      onChange(newValue);
    },
    [onChange, value]
  );

  const selectedLabel = useMemo(() => {
    if (Array.isArray(value)) {
      return value
        .map((v) => options.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(", ");
    }
    return "";
  }, [value, options]);

  if (native) {
    return (
      <div className={`w-full ${className}`} ref={ref}>
        {label && (
          <label className="form-label">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <select
          value={value}
          onChange={(e) => {
            const selectedOptions = Array.from(
              e.target.selectedOptions,
              (option) => option.value
            );
            onChange(selectedOptions);
          }}
          disabled={disabled}
          required={required}
          multiple
          className={`w-full px-4 py-3 rounded-2xl border-2 bg-white transition-all duration-200 ${
            disabled
              ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
              : error
              ? "border-red-300 bg-red-50 focus:ring-4 focus:ring-red-100 focus:border-red-500"
              : "border-gray-200 focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500"
          }`}
        >
          {options
            .map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
        </select>

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  const DropdownContent = () => (
    <div
      ref={dropdownRef}
      className="absolute z-10 w-full mt-2 max-h-80 overflow-y-auto rounded-xl border-2 bg-white shadow-lg"
    >
      <div className="p-2 sticky top-0 bg-white z-10">
        {allowSearch && (
          <input
            type="text"
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-400"
          />
        )}
      </div>
      <ul className="py-1">
        {finalOptions.length > 0 ? (
          finalOptions.map((option, index) => {
            const isSelected = value.includes(option.value);
            return (
              <li
                key={option.value}
                onClick={() => handleOptionSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`px-4 py-2 text-sm cursor-pointer flex items-center justify-between ${
                  highlightedIndex === index ? "bg-green-50" : ""
                }`}
              >
                <span>{option.label}</span>
                {isSelected && (
                  <svg
                    className="w-5 h-5 text-green-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </li>
            );
          })
        ) : (
          <li className="px-4 py-2 text-sm text-gray-500">
            No options found
          </li>
        )}
      </ul>
    </div>
  );

  return (
    <div className={`w-full relative ${className}`} ref={ref}>
      {label && (
        <label className="form-label">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className={`w-full px-4 py-3 text-left rounded-2xl border-2 bg-white transition-all duration-200 flex justify-between items-center ${
          disabled
            ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
            : error
            ? "border-red-300 bg-red-50 focus:ring-4 focus:ring-red-100 focus:border-red-500"
            : "border-gray-200 focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500"
        }`}
      >
        <span className="truncate pr-2">
          {selectedLabel || <span className="text-gray-400">{placeholder}</span>}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${open ? "transform rotate-180" : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {open && (portal ? createPortal(<DropdownContent />, document.body) : <DropdownContent />)}
    </div>
  );
};

export default MultiSelect;
